/**
 * Family Column Pagination Manager
 *
 * Business logic for calculating sliding pagination of family member columns.
 * Uses offset-based pagination - slides 1 column at a time for smooth navigation.
 */

import type { FamilyColumnPaginationConfig } from '@/types/family-column-pagination';
import type { FamilyMemberResource } from '@/types/family-resource';

const DEFAULT_CONFIG: FamilyColumnPaginationConfig = {
  minColumnWidth: 70,
  maxVisibleColumns: 5,
  preferredColumns: 4,
};

export class FamilyColumnPaginationManager {
  private config: FamilyColumnPaginationConfig;

  constructor(config: Partial<FamilyColumnPaginationConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  calculateVisibleColumns(screenWidth: number, timeColumnWidth: number): number {
    const availableWidth = screenWidth - timeColumnWidth;
    const maxPossible = Math.floor(availableWidth / this.config.minColumnWidth);
    return Math.min(
      this.config.maxVisibleColumns,
      Math.max(2, Math.min(maxPossible, this.config.preferredColumns))
    );
  }

  /**
   * Calculate max offset for sliding pagination
   * With sliding, we can have positions: 0, 1, 2, ... (total - visibleColumns)
   */
  calculateMaxOffset(totalMembers: number, visibleColumns: number): number {
    if (totalMembers <= visibleColumns) return 0;
    return totalMembers - visibleColumns;
  }

  getVisibleResources<T extends FamilyMemberResource>(
    resources: T[],
    startOffset: number,
    visibleColumns: number
  ): T[] {
    return resources.slice(startOffset, startOffset + visibleColumns);
  }

  calculateColumnWidth(availableWidth: number, visibleColumns: number): number {
    return Math.floor(availableWidth / visibleColumns);
  }

  shouldShowPagination(totalMembers: number, visibleColumns: number): boolean {
    return totalMembers > visibleColumns;
  }

  clampOffset(offset: number, maxOffset: number): number {
    return Math.max(0, Math.min(offset, maxOffset));
  }

  canGoNext(currentOffset: number, maxOffset: number): boolean {
    return currentOffset < maxOffset;
  }

  canGoPrev(currentOffset: number): boolean {
    return currentOffset > 0;
  }
}
